'use client';

import { type JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import {
    SelectContent,
    SelectIcon,
    SelectItem,
    SelectItemText,
    SelectRoot,
    SelectRootProps,
    SelectTrigger,
    SelectValue,
} from '@videoblocks/design-system';

import { ModelSelectBadge } from './ModelSelectBadge';
import { ModelSelectInfo } from './ModelSelectInfo';

export interface ModelSelectProps extends SelectRootProps {
    modelValue: string;
    setModelValue?: (value: string) => void;
    modelVersion: string;
    modelName: string;
    modelDescription: string;
    disabled?: boolean;
    className?: string;
    id?: string;
    name?: string;
}

export function ModelSelect({
    modelValue,
    setModelValue,
    modelVersion,
    modelName,
    modelDescription,
    disabled,
    className,
    id,
    ...rest
}: ModelSelectProps): JSX.Element {
    return (
        <SelectRoot defaultValue={modelValue} onValueChange={setModelValue} disabled={disabled} {...rest}>
            <SelectTrigger
                className={twMerge('w-full max-w-15 gap-3 rounded-md p-2 pr-4 dark:bg-neutral-dark-200', className)}
                iconSuffix={
                    <SelectIcon
                        className={twMerge(
                            'size-3',
                            disabled && 'dark:text-neutral-dark-600 dark:fill-neutral-dark-600',
                        )}
                    />
                }
                id={id}
            >
                <SelectValue asChild>
                    <div className="flex gap-3">
                        <ModelSelectBadge version={modelVersion} disabled={disabled} />
                        <ModelSelectInfo name={modelName} description={modelDescription} disabled={disabled} />
                    </div>
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                <SelectItem value={modelValue}>
                    <SelectItemText fontSize="sm" fontWeight="semibold">
                        {modelDescription}
                    </SelectItemText>
                </SelectItem>
            </SelectContent>
        </SelectRoot>
    );
}
